import { type Actor } from './board'
import { type RevertRecord, type TurnChangeSet } from './trust'
import { z } from 'zod'

// Sessions: one AI conversation on a board. Its transcript is append-only and
// is exactly what the model was given, in order (DESIGN.md principles 1–2).

export type Effort = 'low' | 'medium' | 'high' | 'max'

export type SessionMeta = {
  id: string
  title: string
  /** 'auto' titles may be replaced by the namer; a person's title never is. */
  titleSource: 'auto' | 'user'
  model: string
  effort: Effort
  createdAt: number
  updatedAt: number
  /** A turn is in flight (or was, when the object restarted mid-turn). */
  running: boolean
  costMicros: number
  /**
   * A conversation driven from outside moot, by an MCP client (MATERIALS.md):
   * its turns are that client's tool calls; nothing here calls a model for it.
   */
  external?: { client: string; key: string }
}

/** A reference to a board file, expanded to its bytes only when calling the API. */
export type FileRef = { type: 'moot_file'; fileKey: string; mime: string; name: string }

export type UserBlock = { type: 'text'; text: string } | FileRef

/** What started a turn: something typed in the chat, and/or board messages handed over. */
export type Trigger = {
  chat?: { text: string; files?: FileRef[] }
  /** Board message ids given to this session. */
  fired?: string[]
  /** An outside client (MCP) started working: it read the board, or made its first change. */
  external?: { client: string }
}

type Base = { seq: number; turnId: string; ts: number }

export type TranscriptEntry =
  | (Base & { kind: 'turn_start'; trigger: Trigger })
  /** The user-role content of the turn, as rendered: board diff, files, messages, chat text. */
  | (Base & {
      kind: 'user'
      content: UserBlock[]
      /** The board part of `content` as data, for people (not sent to the model). */
      board?: BoardView
    })
  | (Base & {
      kind: 'assistant'
      /** Anthropic content blocks, verbatim. */
      content: unknown[]
      stopReason: string | null
      usage: Usage
      model: string
      effort: Effort
    })
  | (Base & { kind: 'tool_results'; results: ToolResult[] })
  /** A note to the model from the system (truncation, resumed after restart). */
  | (Base & { kind: 'notice'; text: string })
  | (Base & {
      kind: 'turn_end'
      error?: string
      /** `context_full`: the conversation no longer fits the model's context window. */
      code?: 'context_full'
      aborted?: boolean
    })

/** One change to the board between two turns, as a person would read it. */
export type BoardChange = {
  op: 'added' | 'moved' | 'edited' | 'changed' | 'removed'
  /** What it is: an item kind, or "edge". */
  kind: string
  id: string
  /** Who did it; empty when not known. */
  by: Actor[]
  /** For removed things, which are no longer there to name: their title or text. */
  label?: string
  /** Why, when a session said so (its tool call's intent). */
  intent?: string
}

export type BoardView =
  { kind: 'snapshot'; count: number } | { kind: 'changes'; changes: BoardChange[] }

export type ToolResult = {
  type: 'tool_result'
  tool_use_id: string
  content: string
  is_error?: boolean
  /**
   * Images that go with the text (a page, a region of one), stored with the
   * board's files under these keys and attached when calling the API.
   */
  images?: { key: string; mime: string }[]
}

export type Usage = {
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens?: number | null
  cache_read_input_tokens?: number | null
}

// ---- live events pushed to browsers while a turn runs (not stored) ----

export type SessionEvent =
  | { t: 'session'; session: SessionMeta }
  | { t: 'entry'; sessionId: string; entry: TranscriptEntry }
  /** Streaming progress inside the current assistant message. */
  | {
      t: 'partial'
      sessionId: string
      turnId: string
      toolUseId: string
      name: string
      input: Record<string, unknown>
    }
  | { t: 'status'; sessionId: string; text: string | null }

// ---- browser -> board object, session commands (JSON text frames, `t` starts with "s.") ----

const sid = z.string().min(1).max(64)
const fileRef = z.object({
  type: z.literal('moot_file'),
  fileKey: z.string().max(200),
  mime: z.string().max(120),
  name: z.string().max(255),
})

export const sessionCommandSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('s.create'),
    model: z.string().max(64).optional(),
    effort: z.enum(['low', 'medium', 'high', 'max']).optional(),
  }),
  z.object({ t: z.literal('s.load'), sessionId: sid }),
  /** What the session's next turn would be told about the board, without starting one. */
  z.object({ t: z.literal('s.peek'), sessionId: sid }),
  z.object({
    t: z.literal('s.send'),
    sessionId: sid,
    text: z.string().max(100_000),
    files: z.array(fileRef).max(20).optional(),
  }),
  /** Hand board messages to a session; sessionId "new" starts a fresh one for them. */
  z.object({ t: z.literal('s.fire'), sessionId: sid, messageIds: z.array(sid).min(1).max(100) }),
  z.object({ t: z.literal('s.stop'), sessionId: sid }),
  /** What one turn changed on the board (DESIGN.md 信任層); answered with s.changes. */
  z.object({
    t: z.literal('s.changes'),
    sessionId: sid,
    turnId: sid,
    /** Also say who changed these refs after the turn (for things it did not change). */
    refs: z.array(z.string().max(80)).max(200).optional(),
  }),
  /** Put back what a turn changed, or one item of it; a new operation by the person. */
  z.object({ t: z.literal('s.revert'), sessionId: sid, turnId: sid, itemId: sid.optional() }),
  /** Undo a revert (itself a revert of what the revert changed). */
  z.object({ t: z.literal('s.unrevert'), revertId: sid }),
  z.object({
    t: z.literal('s.update'),
    sessionId: sid,
    title: z.string().trim().min(1).max(120).optional(),
    model: z.string().max(64).optional(),
    effort: z.enum(['low', 'medium', 'high', 'max']).optional(),
  }),
])
export type SessionCommand = z.infer<typeof sessionCommandSchema>

/** board object -> browser, session state (in addition to SessionEvent). */
export type SessionSync =
  | { t: 's.list'; sessions: SessionMeta[]; fired: Record<string, string[]> }
  | { t: 's.transcript'; sessionId: string; entries: TranscriptEntry[]; queued: Trigger[] }
  | { t: 's.error'; sessionId: string | null; code: string; message: string }
  /** Sent only to the socket that created the session (s.create, or s.fire to "new"). */
  | { t: 's.created'; sessionId: string }
  /** The board was given a title (by the namer); open pages update theirs. */
  | { t: 'board.title'; title: string }
  /**
   * Reply to s.peek: the board part of the next turn, exactly as it would be
   * sent (`text`, null when nothing changed) and as data for people.
   */
  | { t: 's.pending'; sessionId: string; text: string | null; view: BoardView }
  /** Reply to s.changes. */
  | { t: 's.changes'; set: TurnChangeSet }
  /** A session's reverts, sent with its transcript and after every revert. */
  | { t: 's.reverts'; sessionId: string; reverts: RevertRecord[] }
